import { Columns, Heading, Modal, Stack, css, styled, List } from 'canoe';
import debounce from 'lodash/debounce';
import React, { useCallback, useMemo, useState } from 'react';

import { FontItem } from './components/FontItem';
import { FontLink } from './components/FontLink';
import { WebFontResource } from './types';
import { useCustomFonts } from './useCustomFonts';

const LeftColumn = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;

    background-color: ${theme.colors.GlitterGrayL20};
    padding: 40px;
    flex: 1;
    text-align: center;
  `}
`;

const RightColumn = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spaces.s};
    padding-left: 40px;

    flex: 1;
  `}
`;

export interface CustomFontLibraryProps {
  onClose: () => void;
  onSelect: (font: WebFontResource) => void;
}

export const CustomFontLibrary: React.FC<CustomFontLibraryProps> = ({
  onClose,
  onSelect,
}: CustomFontLibraryProps) => {
  const [searchValue, setSearchValue] = useState('');
  const onClearSearch = useCallback(() => {
    setSearchValue('');
  }, []);
  const { fonts, loading, createFontFamily, deleteFontFamily } = useCustomFonts();

  const selectFont = (font: WebFontResource) => {
    onClose();
    onSelect(font);
  };

  const filteredItems = useMemo(() => {
    if (!searchValue) {
      return fonts;
    }

    return fonts?.filter((item) => {
      return item.family.toLowerCase().includes(searchValue?.toLowerCase());
    });
  }, [searchValue, fonts]);

  const updateSearchValue = debounce((e) => {
    setSearchValue(e.target.value);
  }, 300);

  return (
    <Modal onClose={onClose} closeOnClickOutside>
      <Modal.Body>
        <Columns>
          <LeftColumn>
            <FontLink onCreate={createFontFamily} />
          </LeftColumn>
          <RightColumn>
            <Heading size="XS" element="h1">
              Your Fonts
            </Heading>
            <Stack>
              <List
                maxHeight={420}
                searchable
                loading={loading}
                onFilter={updateSearchValue}
                onClearFilter={onClearSearch}
                loadingMessage="Retrieving fonts"
                searchInputProps={{
                  placeholder: `Search fonts`,
                }}
                items={filteredItems || []}
                renderItem={({ item: font, ...listItemProps }) => (
                  <FontItem
                    {...listItemProps}
                    font={font}
                    onSelect={selectFont}
                    onDelete={deleteFontFamily}
                  />
                )}
              />
            </Stack>
          </RightColumn>
        </Columns>
      </Modal.Body>
    </Modal>
  );
};
